import React, { memo } from "react";

import { useSelector } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";

import EncodingList from "./encoding/EncodingList";
import Insights from "./insights/Insights";
import LoginAdminRoot from "./login/LoginAdminRoot";
import OauthFailure from "./login/OauthFailure";
import OauthSuccess from "./login/OauthSuccess";
import MainLayout from "./MainLayout";
import OrganizationConfiguration from "./organization/OrganizationConfiguration";
import ProfileAdmin from "./profile/Profile";
import AdminUserRoutes from "./routes/AdminUserRoutes";
import BulkRoutes from "./routes/BulkRoutes";
import CastingRoutes from "./routes/CastingRoutes";
import ContentRoutes from "./routes/ContentRoutes";
import EncodingProfileRoutes from "./routes/EncodingProfileRoutes";
import FASTChannelRoutes from "./routes/FASTChannelRoutes";
import ProjectRoutes from "./routes/ProjectRoutes";
import {
  ENCODINGS_ROUTE,
  FAST_CHANNELS_ROUTE,
  INSIGHTS_ROUTE,
  LOGIN_ROUTE,
  OAUTH_ROUTE,
  ORGANIZATION_CONFIGURATION_ROUTE,
  PROFILE_ROUTE,
  PROJECTS_ROUTE,
  SIGNUP_ROUTE,
} from "./routes/routes";
import useAbility from "../hooks/useAbility";
import SignupAdmin from "./signup/SignupAdmin";

import type { JSX } from "react";

import type { AppState } from "../reducers";

function Routes(): JSX.Element {
  const { canAccess, getHomePageURL, isFastOnly, isOttOnly } = useAbility();
  const isLoggedIn = useSelector(
    (state: AppState) => state.loginReducer.isLoggedIn,
  );
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const {
    adminUsers: canAccessAdminUsers,
    castings: canAccessCastings,
    contents: canAccessContents,
    fastChannels: canAccessFastChannels,
    insights: canAccessInsights,
    bulks: canAccessBulks,
  } = canAccess(orgaOptions);

  const homePageUrl = getHomePageURL();

  return (
    <Switch>
      <Route
        component={OauthFailure}
        path={`${OAUTH_ROUTE}/failure/:message`}
      />
      <Route
        component={OauthSuccess}
        path={`${OAUTH_ROUTE}/success/:session`}
      />
      <Route component={LoginAdminRoot} path={LOGIN_ROUTE} />
      <Route component={SignupAdmin} path={SIGNUP_ROUTE} />
      {isLoggedIn ? (
        <MainLayout>
          <Route exact path="/">
            <Redirect to={homePageUrl} />
          </Route>
          {canAccessFastChannels ? <FASTChannelRoutes /> : null}
          {!canAccessFastChannels && isOttOnly ? (
            <Route path={FAST_CHANNELS_ROUTE}>
              <Redirect to={homePageUrl} />
            </Route>
          ) : null}
          {canAccessCastings ? <CastingRoutes /> : null}
          {canAccessAdminUsers ? <AdminUserRoutes /> : null}
          {canAccessContents ? <ContentRoutes /> : null}
          {canAccessInsights ? (
            <Route exact component={Insights} path={INSIGHTS_ROUTE} />
          ) : null}
          {canAccessAdminUsers ? (
            <Route
              exact
              component={OrganizationConfiguration}
              path={ORGANIZATION_CONFIGURATION_ROUTE}
            />
          ) : null}
          {canAccessBulks ? <BulkRoutes /> : null}
          <Route exact component={ProfileAdmin} path={PROFILE_ROUTE} />
          <Route exact component={EncodingList} path={ENCODINGS_ROUTE} />
          {isFastOnly ? (
            <Route path={PROJECTS_ROUTE}>
              <Redirect to={homePageUrl} />
            </Route>
          ) : (
            <ProjectRoutes />
          )}
          <EncodingProfileRoutes />
        </MainLayout>
      ) : (
        <Redirect to={LOGIN_ROUTE} />
      )}
    </Switch>
  );
}

export default memo(Routes);
